import { ApiClientError } from "@shared/services/api-client";
import type { IntroDetail, IntroInput } from "./types";

export const LIMITS = { tagline: 160, overview: 2000, audience: 1000, disclaimer: 1000, items: 10, item: 300 } as const;

export const TEXT_FIELDS = ["tagline", "overview", "audience", "disclaimer"] as const;
export const LIST_FIELDS = ["measures", "outcomes", "howItWorks", "tips"] as const;
export type TextField = (typeof TEXT_FIELDS)[number];
export type ListField = (typeof LIST_FIELDS)[number];

/** What the editor holds while typing: lists stay as raw text, one item per line. */
export interface IntroFormValues extends Record<TextField | ListField, string> {
  isPlaceholder: boolean;
}

export type FormErrors = Partial<Record<keyof IntroFormValues, string>>;

export const emptyForm: IntroFormValues = {
  tagline: "",
  overview: "",
  measures: "",
  audience: "",
  outcomes: "",
  howItWorks: "",
  tips: "",
  disclaimer: "",
  isPlaceholder: false,
};

export function parseLines(raw: string): string[] {
  return raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

/** The "placeholder" box starts unchecked: saving as an admin turns the flag off unless asked otherwise. */
export function toForm(d: IntroDetail): IntroFormValues {
  return {
    tagline: d.tagline,
    overview: d.overview,
    measures: d.measures.join("\n"),
    audience: d.audience,
    outcomes: d.outcomes.join("\n"),
    howItWorks: d.howItWorks.join("\n"),
    tips: d.tips.join("\n"),
    disclaimer: d.disclaimer,
    isPlaceholder: false,
  };
}

export function toInput(v: IntroFormValues): IntroInput {
  return {
    tagline: v.tagline.trim(),
    overview: v.overview.trim(),
    measures: parseLines(v.measures),
    audience: v.audience.trim(),
    outcomes: parseLines(v.outcomes),
    howItWorks: parseLines(v.howItWorks),
    tips: parseLines(v.tips),
    disclaimer: v.disclaimer.trim(),
    isPlaceholder: v.isPlaceholder,
  };
}

export function validateForm(v: IntroFormValues): FormErrors {
  const errors: FormErrors = {};
  const tagline = v.tagline.trim();
  const overview = v.overview.trim();
  if (!tagline) errors.tagline = "Tagline wajib diisi.";
  else if (tagline.length > LIMITS.tagline) errors.tagline = `Tagline maksimal ${LIMITS.tagline} karakter.`;
  if (!overview) errors.overview = "Ikhtisar wajib diisi.";
  else if (overview.length > LIMITS.overview) errors.overview = `Ikhtisar maksimal ${LIMITS.overview} karakter.`;
  if (v.audience.trim().length > LIMITS.audience) errors.audience = `Maksimal ${LIMITS.audience} karakter.`;
  if (v.disclaimer.trim().length > LIMITS.disclaimer) errors.disclaimer = `Maksimal ${LIMITS.disclaimer} karakter.`;

  for (const field of LIST_FIELDS) {
    const items = parseLines(v[field]);
    const tooLong = items.findIndex((item) => item.length > LIMITS.item);
    if (items.length > LIMITS.items) errors[field] = `Maksimal ${LIMITS.items} butir.`;
    else if (tooLong >= 0) errors[field] = `Butir ke-${tooLong + 1} melebihi ${LIMITS.item} karakter.`;
  }
  return errors;
}

/** Compares what would be sent, so spacing and blank lines do not count as an edit. */
export function isDirty(form: IntroFormValues, baseline: IntroFormValues): boolean {
  return JSON.stringify(toInput(form)) !== JSON.stringify(toInput(baseline));
}

const FIELDS: readonly string[] = [...TEXT_FIELDS, ...LIST_FIELDS, "isPlaceholder"];

/** Joi reports { field, message }; a list item arrives as "measures.2". Put each message next to its input. */
export function fieldErrorsFrom(err: unknown): FormErrors {
  const out: FormErrors = {};
  if (!(err instanceof ApiClientError) || !Array.isArray(err.details)) return out;
  for (const d of err.details as Array<{ field?: unknown; message?: unknown } | null>) {
    if (!d || typeof d.message !== "string") continue;
    const [name, index] = String(d.field ?? "").split(".");
    if (name === undefined || !FIELDS.includes(name)) continue;
    const key = name as keyof IntroFormValues;
    const position = Number(index);
    out[key] ??= index !== undefined && Number.isInteger(position) ? `Butir ke-${position + 1}: ${d.message}` : d.message;
  }
  return out;
}
